import React, { useState, useEffect, useRef } from 'react';
import { Card, Switch, Spin, message, Alert, Space, InputNumber, Divider, Tag, Input, Button, Row, Col, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';
import PersianDatePicker from './common/PersianDatePicker';

const TOGGLE_FIELDS = [
  {
    key: 'waitlistEnabledFa',
    title: 'لیست انتظار (فارسی) فعال است',
    desc: 'اگر روشن باشد، کاربران فارسی‌زبان بدون دعوت‌نامه می‌توانند وارد لیست انتظار شوند.'
  },
  {
    key: 'waitlistEnabledEn',
    title: 'لیست انتظار (انگلیسی) فعال است',
    desc: 'اگر روشن باشد، کاربران انگلیسی‌زبان بدون دعوت‌نامه می‌توانند وارد لیست انتظار شوند.'
  },
  {
    key: 'waitlistShowCount',
    title: 'نمایش تعداد نفرات در انتظار روی سایت',
    desc: 'اگر روشن باشد، در بخش لیست انتظار صفحه‌ی اصلی نوشته می‌شود چند نفر تاکنون (تأییدشده) در انتظار هستند.'
  },
  {
    key: 'waitlistShowCapacity',
    title: 'نمایش ظرفیت کل و جای خالی روی سایت',
    desc: 'اگر روشن باشد (و ظرفیت > ۰ تنظیم شده باشد)، ظرفیت کل ثبت‌نام و تعداد جای خالی باقی‌مانده نمایش داده می‌شود.'
  },
  {
    key: 'waitlistShowCountdownSeconds',
    title: 'نمایش شمارنده‌ی ثانیه‌ای مهلت ثبت‌نام',
    desc: 'اگر روشن باشد، زیر تایمر مهلت لیست انتظار، مجموع ثانیه‌های باقی‌مانده هم به‌صورت شمارنده‌ی زنده نمایش داده می‌شود.'
  },
  {
    key: 'registrationOpen',
    title: 'ثبت‌نام عمومی فعال است',
    desc: 'اگر روشن باشد، کاربر حتی بدون دعوت‌نامه هم می‌تواند مسیر ثبت‌نام را طی کند (شانس بسیار پایین برای عبور از سکو).'
  },
  {
    key: 'requireInviteCodeOnLogin',
    title: 'الزام کد دعوت در صفحه ورود (پورتال ۹ و ۳/۴)',
    desc: 'اگر روشن باشد، در صفحه‌ی ورود مخفی، کاربرانی که هنوز عضو سایت نیستند باید علاوه بر شماره تماس، کد دعوت خود را هم وارد کنند تا بتوانند وارد شوند. کاربران قبلی همیشه فقط با شماره وارد می‌شوند — مستقل از تنظیم «ثبت‌نام عمومی» بالا.'
  },
  {
    key: 'showFinancialSupport',
    title: 'نمایش حمایت مالی',
    desc: 'نمایش بخش حمایت مالی در سایت.'
  },
  {
    key: 'showFooterLoginButton',
    title: 'نمایش دکمه ورود در فوتر',
    desc: 'اگر خاموش باشد، دکمه «ورود» در فوتر سایت مخفی می‌ماند.'
  },
  {
    key: 'showLoginButton',
    title: 'نمایش دکمه ورود در هدر سایت',
    desc: 'اگر خاموش باشد، دکمه «ورود» در نوار بالای سایت مخفی می‌ماند.'
  },
  {
    key: 'invitationRequired',
    title: 'کد دعوت برای ثبت‌نام مستقیم الزامی است',
    desc: 'کنترل قدیمی‌تر مسیر ثبت‌نام مستقیم با کد — مستقل از منطق جدید لیست انتظار/دعوت.'
  }
];

const NUMBER_FIELDS = [
  { key: 'waitlistCapacity', title: 'ظرفیت کل لیست انتظار (۰ = نامحدود)', min: 0, max: 1000000 },
  { key: 'waitingDaysBeforeInvite', title: 'تعداد روز انتظار قبل از ارسال خودکار دعوت‌نامه', min: 1, max: 365 },
  { key: 'platformMaxAttempts', title: 'تعداد دفعات مجاز تلاش برای عبور از سکوی ۹ و ۳/۴', min: 1, max: 20 },
  { key: 'initialGalleons', title: 'گالیون هدیه هنگام ثبت‌نام', min: 0, max: 1000 },
  { key: 'invitationGalleons', title: 'گالیون هدیه برای خرید وسایل (صفحه پیام محرمانه)', min: 0, max: 1000 }
];

const PROBABILITY_LABELS = {
  admin_direct_invite: 'دعوت مستقیم مدیر/استاد',
  waitlist_auto_invite: 'آمده از لیست انتظار',
  friend_referral: 'دعوت یک کاربر دیگر (رفرال)',
  public_registration: 'ثبت‌نام عمومی',
  purchased: 'خرید بسته کامل ورود'
};

// تبدیل ارقام فارسی/عربی به لاتین — Ant Design InputNumber ارقام غیرلاتین را
// نمی‌شناسد و onChange(null) برمی‌گرداند. با parser سفارشی این مشکل حل می‌شود.
const PERSIAN_DIGITS_MAP = '۰۱۲۳۴۵۶۷۸۹';
const ARABIC_DIGITS_MAP  = '٠١٢٣٤٥٦٧٨٩';
function parseFarsiNumber(displayValue) {
  if (displayValue == null || displayValue === '') return displayValue;
  return String(displayValue).replace(/[۰-۹٠-٩]/g, (ch) => {
    const pi = PERSIAN_DIGITS_MAP.indexOf(ch);
    if (pi !== -1) return String(pi);
    const ai = ARABIC_DIGITS_MAP.indexOf(ch);
    return ai !== -1 ? String(ai) : ch;
  });
}

export default function RegistrationSettingsManager() {
  const [settings, setSettings] = useState({});
  const [probabilities, setProbabilities] = useState({});
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState({ key: '', label: '', color: '#722f37' });
  // تعداد فعلی تأییدشده‌های لیست انتظار — کنار فیلد ظرفیت نمایش داده می‌شود
  const [waitlistCount, setWaitlistCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // مقادیر فیلدهای عددی هنگام تایپ فقط لوکال نگه داشته می‌شوند و تنها با
  // خروج از فیلد (blur) یا Enter ذخیره می‌شوند — تا هر کلیدی یک درخواست
  // ذخیره‌سازی جداگانه نفرستد (که باعث ریس‌کاندیشن و از دست رفتن مقدار
  // نهایی هنگام رفرش سریع صفحه می‌شد).
  const [numberDrafts, setNumberDrafts] = useState({});
  const numberDraftsRef = useRef({});
  // مقدار پیش‌نویس تاریخ بسته‌شدن لیست انتظار — تا وقتی picker باز است فقط
  // لوکال نگه داشته می‌شود و هنگام بسته‌شدن picker ذخیره می‌شود.
  const closeAtDraftRef = useRef(undefined);
  const [closeAtDraft, setCloseAtDraft] = useState(undefined);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, probRes, tagsRes] = await Promise.all([
        adminAPI.getRegistrationSettings(),
        adminAPI.getAppConfig('platform_cross_probabilities'),
        adminAPI.getAppConfig('user_tags')
      ]);
      if (settingsRes.data.success) {
        setSettings(settingsRes.data.settings);
        setWaitlistCount(settingsRes.data.waitlistVerifiedCount ?? null);
        setNumberDrafts({});
        numberDraftsRef.current = {};
        closeAtDraftRef.current = undefined;
        setCloseAtDraft(undefined);
      }
      if (probRes.data.success) setProbabilities(probRes.data.config?.value || {});
      if (tagsRes.data.success) setTags(tagsRes.data.config?.value || []);
    } catch (error) {
      message.error('خطا در بارگذاری تنظیمات');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    setSaving(true);
    try {
      const response = await adminAPI.updateRegistrationSettings(patch);
      if (response.data.success) {
        setSettings(response.data.settings);
        message.success('تنظیمات ذخیره شد');
      }
    } catch (error) {
      message.error('خطا در ذخیره تنظیمات');
      loadAll();
    } finally {
      setSaving(false);
    }
  };

  const commitNumberField = (field) => {
    // از ref می‌خوانیم تا آخرین مقدار بلافاصله در دسترس باشد — حتی اگر React
    // هنوز state را به‌روز نکرده باشد (batching در React 18).
    const draft = numberDraftsRef.current[field.key];
    if (draft === undefined) return;
    if (draft === null || Number.isNaN(Number(draft))) {
      delete numberDraftsRef.current[field.key];
      setNumberDrafts((prev) => { const next = { ...prev }; delete next[field.key]; return next; });
      return;
    }
    const clamped = Math.min(field.max, Math.max(field.min, Number(draft)));
    delete numberDraftsRef.current[field.key];
    setNumberDrafts((prev) => { const next = { ...prev }; delete next[field.key]; return next; });
    if (clamped !== settings[field.key]) {
      updateSettings({ [field.key]: clamped });
    }
  };

  const saveProbabilities = async (next) => {
    setProbabilities(next);
    try {
      await adminAPI.upsertAppConfig('platform_cross_probabilities', next);
      message.success('احتمال‌ها ذخیره شد');
    } catch (error) {
      message.error('خطا در ذخیره احتمال‌ها');
    }
  };

  const saveTags = async (next) => {
    setTags(next);
    try {
      await adminAPI.upsertAppConfig('user_tags', next);
      message.success('لیست Tagها به‌روز شد');
    } catch (error) {
      message.error('خطا در ذخیره Tagها');
    }
  };

  const addTag = () => {
    if (!newTag.key.trim() || !newTag.label.trim()) {
      message.warning('کلید و عنوان Tag الزامی است');
      return;
    }
    if (tags.some((t) => t.key === newTag.key.trim())) {
      message.warning('این کلید قبلاً وجود دارد');
      return;
    }
    const next = [...tags, { ...newTag, key: newTag.key.trim() }];
    saveTags(next);
    setNewTag({ key: '', label: '', color: '#722f37' });
  };

  const removeTag = (key) => {
    saveTags(tags.filter((t) => t.key !== key));
  };

  if (loading) return <Spin style={{ display: 'block', margin: '48px auto' }} />;

  return (
    <div style={{ maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card title="تنظیمات عمومی">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          message="کنترل کامل مسیر ثبت‌نام، دعوت و لیست انتظار"
          description="هر مقداری که ممکن است در آینده تغییر کند از همین‌جا قابل مدیریت است — هیچ مقدار ثابتی در کد وجود ندارد."
        />
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {TOGGLE_FIELDS.map((f) => (
            <div key={f.key} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{f.title}</div>
                <div style={{ color: '#888', fontSize: 13 }}>{f.desc}</div>
              </div>
              <Switch checked={!!settings[f.key]} loading={saving} onChange={(v) => updateSettings({ [f.key]: v })} />
            </div>
          ))}
        </Space>

        <Divider />

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>تاریخ/ساعت بسته‌شدن خودکار لیست انتظار (اختیاری)</div>
          <div style={{ color: '#888', fontSize: 13, marginBottom: 8 }}>
            اگر تنظیم شود، بعد از این زمان لیست انتظار برای هر دو زبان به‌طور خودکار بسته می‌شود — صرف‌نظر از دو کلید بالا. برای بدون‌محدودیت، خالی بگذار.
          </div>
          <Space>
            <div style={{ minWidth: 220 }}>
              <PersianDatePicker
                value={closeAtDraft !== undefined ? closeAtDraft : settings.waitlistCloseAt}
                withTime
                onChange={(v) => {
                  const val = v || null;
                  closeAtDraftRef.current = val;
                  setCloseAtDraft(val);
                }}
                onClose={() => {
                  if (closeAtDraftRef.current !== undefined) {
                    updateSettings({ waitlistCloseAt: closeAtDraftRef.current });
                    closeAtDraftRef.current = undefined;
                    setCloseAtDraft(undefined);
                  }
                }}
              />
            </div>
            {(closeAtDraft !== undefined ? closeAtDraft : settings.waitlistCloseAt) && (
              <Button size="small" onClick={() => { closeAtDraftRef.current = undefined; setCloseAtDraft(undefined); updateSettings({ waitlistCloseAt: null }); }}>پاک کردن</Button>
            )}
          </Space>
        </div>

        <Divider />

        <Row gutter={[16, 16]}>
          {NUMBER_FIELDS.map((f) => (
            <Col xs={24} sm={12} key={f.key}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>{f.title}</div>
              <InputNumber
                style={{ width: '100%' }}
                min={f.min}
                max={f.max}
                parser={parseFarsiNumber}
                value={numberDrafts[f.key] !== undefined ? numberDrafts[f.key] : settings[f.key]}
                onChange={(v) => {
                  numberDraftsRef.current[f.key] = v;
                  setNumberDrafts((prev) => ({ ...prev, [f.key]: v }));
                }}
                onBlur={() => commitNumberField(f)}
                onPressEnter={() => commitNumberField(f)}
              />
              {f.key === 'waitlistCapacity' && waitlistCount !== null && (
                <div style={{ color: '#888', fontSize: 13, marginTop: 4 }}>
                  هم‌اکنون {waitlistCount} نفر (تأییدشده) در لیست انتظار هستند
                  {Number(settings.waitlistCapacity) > 0 && (
                    <> — {Math.max(0, Number(settings.waitlistCapacity) - waitlistCount)} جای خالی باقی مانده</>
                  )}
                </div>
              )}
            </Col>
          ))}
        </Row>
      </Card>

      <Card title="احتمال موفقیت عبور از سکوی ۹ و ۳/۴ (درصد)">
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
          message="این اعداد بر اساس اینکه کاربر از چه مسیری وارد شده محاسبه می‌شود — هیچ عددی هاردکد نیست."
        />
        <Row gutter={[16, 16]}>
          {Object.keys(PROBABILITY_LABELS).map((key) => (
            <Col xs={24} sm={12} key={key}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>{PROBABILITY_LABELS[key]}</div>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                addonAfter="%"
                parser={parseFarsiNumber}
                value={probabilities[key]}
                onChange={(v) => saveProbabilities({ ...probabilities, [key]: v })}
              />
            </Col>
          ))}
        </Row>
      </Card>

      <Card title="Tagهای قابل استفاده برای کاربران">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Tag هیچ ارتباطی با Journey ندارد — هر کاربر می‌تواند همزمان چند Tag داشته باشد."
        />
        <Space wrap style={{ marginBottom: 16 }}>
          {tags.map((t) => (
            <Tag key={t.key} color={t.color} closable onClose={(e) => { e.preventDefault(); removeTag(t.key); }}>
              {t.label} <span style={{ opacity: 0.7 }}>({t.key})</span>
            </Tag>
          ))}
        </Space>
        <Space wrap>
          <Input placeholder="کلید (مثلا vip)" value={newTag.key} onChange={(e) => setNewTag({ ...newTag, key: e.target.value })} style={{ width: 140 }} />
          <Input placeholder="عنوان فارسی" value={newTag.label} onChange={(e) => setNewTag({ ...newTag, label: e.target.value })} style={{ width: 140 }} />
          <Input type="color" value={newTag.color} onChange={(e) => setNewTag({ ...newTag, color: e.target.value })} style={{ width: 60, padding: 2 }} />
          <Button icon={<PlusOutlined />} onClick={addTag}>افزودن Tag</Button>
        </Space>
      </Card>
    </div>
  );
}
